<template>
	<section id="fichiers-azure"><br>
		<b-row>
			<b-col>
				<b-card>
					<b-card-title>Migration des fichiers vers Azure</b-card-title>
					<b-card-text class="mb-1">
						Recapitulatif par centre des fichiers stockes en local
						(non remontes) et sur <code>ubicxsecure/bbc-clients</code>.
						Le bouton <b>Verifier les tailles</b> compare la valeur
						<code>I_taille</code> en base avec le <code>contentLength</code>
						reel du blob (HEAD Azure, concurrence 10). Apres detection
						d'ecarts, un bouton <b>Corriger en base</b> aligne
						<code>I_taille</code> sur la taille reelle Azure.
					</b-card-text>

					<div v-if="loading" class="text-center my-3">
						<b-spinner small /> Chargement du recap...
					</div>

					<div v-else>
						<div class="d-flex flex-wrap mb-2">
							<b-badge variant="light-secondary" class="mr-1 mb-1">
								Centres : {{ rows.length }}
							</b-badge>
							<b-badge variant="light-warning" class="mr-1 mb-1">
								Fichiers locaux : {{ totalNbLocal }} ({{ formatBytes(totalTailleLocal) }})
							</b-badge>
							<b-badge variant="light-primary" class="mr-1 mb-1">
								Fichiers Azure : {{ totalNbAzure }} ({{ formatBytes(totalTailleAzure) }})
							</b-badge>
						</div>

						<vue-good-table
							:columns="columns"
							:rows="rows"
							:pagination-options="{
								enabled: true,
								mode: 'records',
								perPage: 25,
								position: 'top',
								perPageDropdown: [10, 25, 50],
								dropdownAllowAll: true,
								nextLabel: 'Suivant',
								prevLabel: 'Precedent',
								rowsPerPageLabel: 'lignes par page',
								ofLabel: 'de',
								pageLabel: 'page',
								allLabel: 'Tout',
							}"
							:search-options="{ enabled: true, placeholder: 'Recherche centre' }"
							:sort-options="{ enabled: true, initialSortBy: { field: 'C_libelle', type: 'asc' } }"
						>
							<template slot="table-row" slot-scope="props">
								<span v-if="props.column.field === 'nb_local'">
									<b-badge :variant="props.row.nb_local > 0 ? 'light-warning' : 'light-success'">
										{{ props.row.nb_local }}
									</b-badge>
									<small class="text-muted ml-50">{{ formatBytes(props.row.taille_local) }}</small>
								</span>
								<span v-else-if="props.column.field === 'nb_azure'">
									{{ props.row.nb_azure }}
									<small class="text-muted ml-50">{{ formatBytes(props.row.taille_azure) }}</small>
								</span>
								<span v-else-if="props.column.field === 'check'">
									<b-button
										v-if="props.row.nb_azure > 0"
										size="sm"
										variant="outline-primary"
										:disabled="isBusy(props.row.C_UI)"
										@click="verifySizes(props.row)"
									>
										<b-spinner v-if="isBusy(props.row.C_UI)" small />
										<span v-else>Verifier les tailles</span>
									</b-button>
									<small v-else class="text-muted">Aucun fichier migre</small>
								</span>
								<span v-else-if="props.column.field === 'last'">
									<template v-if="lastChecks[props.row.C_UI]">
										<b-badge
											v-if="isLastCheckOk(props.row.C_UI)"
											variant="light-success"
										>OK ({{ lastChecks[props.row.C_UI].nb_ok }} / {{ lastChecks[props.row.C_UI].nb_checked }})</b-badge>
										<b-badge v-else variant="light-danger">
											{{ lastChecks[props.row.C_UI].nb_mismatch }} ecart(s),
											{{ lastChecks[props.row.C_UI].nb_missing }} manquant(s)
											<span v-if="lastChecks[props.row.C_UI].nb_error > 0">, {{ lastChecks[props.row.C_UI].nb_error }} erreur(s)</span>
										</b-badge>
										<b-button
											v-if="!isLastCheckOk(props.row.C_UI)"
											size="sm"
											variant="link"
											class="ml-50 p-0"
											@click="showDetail(props.row.C_UI)"
										>
											Detail
										</b-button>
									</template>
									<small v-else class="text-muted">-</small>
								</span>
								<span v-else>
									{{ props.formattedRow[props.column.field] }}
								</span>
							</template>
						</vue-good-table>
					</div>
				</b-card>
			</b-col>
		</b-row>

		<b-row v-if="detailCUIS && lastChecks[detailCUIS]">
			<b-col>
				<b-card no-body>
					<b-card-body>
						<b-card-title>
							Detail du controle &mdash; {{ centreLibelle(detailCUIS) }}
							<b-button size="sm" variant="link" class="float-right" @click="detailCUIS = null">Fermer</b-button>
						</b-card-title>
						<b-card-text>
							{{ lastChecks[detailCUIS].nb_checked }} fichier(s) verifie(s) :
							{{ lastChecks[detailCUIS].nb_ok }} OK,
							{{ lastChecks[detailCUIS].nb_mismatch }} ecart(s) de taille,
							{{ lastChecks[detailCUIS].nb_missing }} blob(s) introuvable(s),
							{{ lastChecks[detailCUIS].nb_error }} erreur(s) HTTP.
							<b-badge v-if="lastChecks[detailCUIS].detail_truncated" variant="light-warning" class="ml-1">
								Detail tronque a 200 lignes par categorie
							</b-badge>
						</b-card-text>

						<div v-if="lastChecks[detailCUIS].mismatches.length > 0">
							<div class="d-flex align-items-center mb-1">
								<h5 class="mb-0">Ecarts de taille ({{ lastChecks[detailCUIS].nb_mismatch }})</h5>
								<b-button
									size="sm"
									variant="primary"
									class="ml-2"
									:disabled="isFixBusy(detailCUIS)"
									@click="fixSizes(detailCUIS)"
								>
									<b-spinner v-if="isFixBusy(detailCUIS)" small />
									<span v-else>Corriger en base</span>
								</b-button>
								<small class="text-muted ml-1">
									Met a jour <code>I_taille</code> avec la taille reelle du blob (re-HEAD cote serveur).
									Limite a {{ lastChecks[detailCUIS].mismatches.length }} ecart(s) visible(s).
								</small>
							</div>
							<b-table
								:items="lastChecks[detailCUIS].mismatches"
								:fields="mismatchFields"
								small
								striped
								responsive
								class="mb-2"
							>
								<template #cell(taille_db)="data">{{ formatBytes(data.value) }} ({{ data.value }} o)</template>
								<template #cell(taille_azure)="data">{{ formatBytes(data.value) }} ({{ data.value }} o)</template>
							</b-table>
						</div>

						<div v-if="lastChecks[detailCUIS].missing.length > 0">
							<h5>Blobs introuvables sur Azure ({{ lastChecks[detailCUIS].nb_missing }})</h5>
							<b-table
								:items="lastChecks[detailCUIS].missing"
								:fields="missingFields"
								small
								striped
								responsive
								class="mb-2"
							>
								<template #cell(taille_db)="data">{{ formatBytes(data.value) }}</template>
							</b-table>
						</div>

						<div v-if="lastChecks[detailCUIS].errors.length > 0">
							<h5>Erreurs HEAD Azure ({{ lastChecks[detailCUIS].nb_error }})</h5>
							<b-table
								:items="lastChecks[detailCUIS].errors"
								:fields="errorFields"
								small
								striped
								responsive
							/>
						</div>
					</b-card-body>
				</b-card>
			</b-col>
		</b-row>
	</section>
</template>

<script>
import {
	BRow, BCol, BCard, BCardBody, BCardTitle, BCardText,
	BButton, BBadge, BSpinner, BTable,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
	components: {
		BRow, 
		BCol, 
		BCard, 
		BCardBody, 
		BCardTitle, 
		BCardText,
		BButton, 
		BBadge, 
		BSpinner, 
		BTable,
		VueGoodTable,
	},
	data() {
		return {
			loading: true,
			rows: [],
			lastChecks: {},
			busyCUIS: {},
			fixBusyCUIS: {},
			detailCUIS: null,
			columns: [
				{ label: 'Centre', field: 'C_libelle' },
				{ label: 'Societe', field: 'C_rs' },
				{ label: 'Locaux (non migres)', field: 'nb_local', type: 'number' },
				{ label: 'Migres Azure', field: 'nb_azure', type: 'number' },
				{ label: 'Controle', field: 'check', sortable: false },
				{ label: 'Dernier resultat', field: 'last', sortable: false },
			],
			mismatchFields: [
				{ key: 'ID_fichiers', label: 'ID' },
				{ key: 'C_path', label: 'Blob path' },
				{ key: 'taille_db', label: 'Taille DB' },
				{ key: 'taille_azure', label: 'Taille Azure' },
			],
			missingFields: [
				{ key: 'ID_fichiers', label: 'ID' },
				{ key: 'C_path', label: 'Blob path' },
				{ key: 'taille_db', label: 'Taille DB' },
			],
			errorFields: [
				{ key: 'ID_fichiers', label: 'ID' },
				{ key: 'C_path', label: 'Blob path' },
				{ key: 'error', label: 'Erreur' },
			],
		}
	},
	computed: {
		totalNbLocal() { return this.rows.reduce((a, r) => a + (r.nb_local || 0), 0) },
		totalTailleLocal() { return this.rows.reduce((a, r) => a + (r.taille_local || 0), 0) },
		totalNbAzure() { return this.rows.reduce((a, r) => a + (r.nb_azure || 0), 0) },
		totalTailleAzure() { return this.rows.reduce((a, r) => a + (r.taille_azure || 0), 0) },
	},
	created() {
		this.loadRecap()
	},
	methods: {
		formatBytes(bytes) {
			const n = Number(bytes) || 0
			if (n === 0) return '0 o'
			const units = ['o', 'Ko', 'Mo', 'Go', 'To']
			const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1)
			return `${(n / (1024 ** i)).toFixed(i === 0 ? 0 : 2)} ${units[i]}`
		},
		isBusy(CUIS) { return !!this.busyCUIS[CUIS] },
		isFixBusy(CUIS) { return !!this.fixBusyCUIS[CUIS] },
		isLastCheckOk(CUIS) {
			const c = this.lastChecks[CUIS]
			return c && c.nb_mismatch === 0 && c.nb_missing === 0 && c.nb_error === 0
		},
		centreLibelle(CUIS) {
			const r = this.rows.find(x => x.C_UI === CUIS)
			return r ? r.C_libelle : CUIS
		},
		loadRecap() {
			this.loading = true
			this.$http.get('/admin/fichiers-azure/recap')
				.then(response => {
					this.loading = false
					if (response.data && response.data.res === 1) {
						this.rows = response.data.ResultRecap || []
					} else {
						this.notifyError('Recap impossible', (response.data && response.data.error) || 'Reponse inattendue')
					}
				})
				.catch(err => {
					this.loading = false
					this.notifyError('Recap impossible', this.errorDetail(err))
				})
		},
		setBusy(CUIS, value) { this.$set(this.busyCUIS, CUIS, value) },
		setFixBusy(CUIS, value) { this.$set(this.fixBusyCUIS, CUIS, value) },
		setLastCheck(CUIS, value) { this.$set(this.lastChecks, CUIS, value) },
		verifySizes(row) {
			const CUIS = row.C_UI
			if (!CUIS || this.isBusy(CUIS)) { return }
			this.setBusy(CUIS, true)
			this.setLastCheck(CUIS, null)
			this.$http.post('/admin/fichiers-azure/verify-sizes', { CUIS })
				.then(response => {
					this.setBusy(CUIS, false)
					if (response.data && response.data.res === 1) {
						const lc = {
							nb_checked: response.data.nb_checked,
							nb_ok: response.data.nb_ok,
							nb_mismatch: response.data.nb_mismatch,
							nb_missing: response.data.nb_missing,
							nb_error: response.data.nb_error || 0,
							detail_truncated: !!response.data.detail_truncated,
							mismatches: response.data.mismatches || [],
							missing: response.data.missing || [],
							errors: response.data.errors || [],
						}
						this.setLastCheck(CUIS, lc)
						const ko = lc.nb_mismatch + lc.nb_missing + lc.nb_error
						if (ko === 0) {
							this.notifySuccess('Controle OK', `${row.C_libelle} : ${lc.nb_ok} fichier(s) coherent(s)`)
						} else {
							this.notifyWarning(
								'Ecarts detectes',
								`${row.C_libelle} : ${lc.nb_mismatch} ecart(s) de taille, ${lc.nb_missing} manquant(s)`,
							)
							this.detailCUIS = CUIS
						}
					} else {
						this.notifyError('Verification impossible', (response.data && response.data.error) || 'Reponse inattendue')
					}
				})
				.catch(err => {
					this.setBusy(CUIS, false)
					this.notifyError('Verification impossible', this.errorDetail(err))
				})
		},
		showDetail(CUIS) {
			this.detailCUIS = CUIS
			this.$nextTick(() => {
				const el = document.getElementById('fichiers-azure')
				if (el) {
					const cards = el.querySelectorAll('.card')
					if (cards.length > 1) { cards[cards.length - 1].scrollIntoView({ behavior: 'smooth', block: 'start' }) }
				}
			})
		},
		fixSizes(CUIS) {
			const lc = this.lastChecks[CUIS]
			if (!lc || lc.mismatches.length === 0 || this.isFixBusy(CUIS)) { return }
			const ids = lc.mismatches.map(m => m.ID_fichiers)
			if (!window.confirm(`Aligner I_taille avec la taille reelle Azure pour ${ids.length} fichier(s) ? Cette action met a jour TB_FICHIERS.`)) { return }
			this.setFixBusy(CUIS, true)
			this.$http.post('/admin/fichiers-azure/fix-sizes', { CUIS, IDs: ids })
				.then(response => {
					this.setFixBusy(CUIS, false)
					if (response.data && response.data.res === 1) {
						this.notifySuccess(
							'Tailles corrigees',
							`${response.data.nb_fixed} mise(s) a jour, ${response.data.nb_skipped} ignore(s), ${response.data.nb_error} erreur(s)`,
						)
						const row = this.rows.find(x => x.C_UI === CUIS)
						if (row) { this.verifySizes(row) }
					} else {
						this.notifyError('Correction impossible', (response.data && response.data.error) || 'Reponse inattendue')
					}
				})
				.catch(err => {
					this.setFixBusy(CUIS, false)
					this.notifyError('Correction impossible', this.errorDetail(err))
				})
		},
		errorDetail(err) {
			return (err && err.response && err.response.data && (err.response.data.detail || err.response.data.error))
				|| (err && err.message)
				|| 'Erreur reseau'
		},
		notifySuccess(title, text) {
			this.$toast({
				component: ToastificationContent,
				props: { title, icon: 'CheckIcon', variant: 'success', text },
			}, { timeout: 5000, position: 'top-center' })
		},
		notifyWarning(title, text) {
			this.$toast({
				component: ToastificationContent,
				props: { title, icon: 'AlertTriangleIcon', variant: 'warning', text },
			}, { timeout: 6000, position: 'top-center' })
		},
		notifyError(title, text) {
			this.$toast({
				component: ToastificationContent,
				props: { title, icon: 'AlertTriangleIcon', variant: 'danger', text },
			}, { timeout: 8000, position: 'top-center' })
		},
	},
}
</script>
